import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Rocket, Github, Globe, Server, Loader2, Plus, Trash2, Play, RefreshCw,
  CheckCircle, XCircle, Circle, MinusCircle, AlertTriangle, ChevronRight,
  ChevronLeft, KeyRound, UploadCloud, Copy
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20",
  running: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  completed: "text-green-400 bg-green-500/10 border-green-500/20",
  failed: "text-red-400 bg-red-500/10 border-red-500/20",
  cancelled: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
};

function StepIcon({ status }) {
  if (status === "done") return <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />;
  if (status === "failed") return <XCircle className="w-4 h-4 text-red-400 shrink-0" />;
  if (status === "running") return <Loader2 className="w-4 h-4 text-blue-400 animate-spin shrink-0" />;
  if (status === "skipped") return <MinusCircle className="w-4 h-4 text-yellow-400 shrink-0" />;
  return <Circle className="w-4 h-4 text-zinc-700 shrink-0" />;
}

function ProgressView({ dep }) {
  const [openLog, setOpenLog] = useState(null);
  return (
    <div className="space-y-1" data-testid={`deployment-progress-${dep.id}`}>
      {(dep.steps || []).map((s) => (
        <div key={s.key}>
          <button
            onClick={() => setOpenLog(openLog === s.key ? null : s.key)}
            className="w-full flex items-center gap-2 text-left py-1 px-2 rounded hover:bg-white/5"
            data-testid={`step-${s.key}`}>
            <StepIcon status={s.status} />
            <span className={`text-xs ${s.status === "pending" ? "text-zinc-600" : "text-zinc-300"}`}>{s.label}</span>
            {s.status === "skipped" && <span className="text-[10px] text-yellow-500">skipped</span>}
          </button>
          {openLog === s.key && s.log && (
            <pre className="text-[10px] text-zinc-400 bg-black/50 rounded p-2 ml-6 mb-1 max-h-40 overflow-auto whitespace-pre-wrap">{s.log}</pre>
          )}
        </div>
      ))}
    </div>
  );
}

function DeploymentCard({ dep, onRefresh }) {
  const [expanded, setExpanded] = useState(dep.status === "running");
  const [busy, setBusy] = useState(false);

  const act = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      if (action === "delete") {
        await axios.delete(`${API}/deployments/${dep.id}`);
        toast.success("Deployment record deleted");
      } else if (action === "start") {
        const isExisting = dep.target_mode === "existing_server";
        if (isExisting && !window.confirm(
          `⚠️ This will install packages (Nginx, MongoDB, Node...) and services on the LIVE server ${dep.server_ip || ""}. Existing apps are not removed, but proceed carefully. Continue?`)) {
          setBusy(false); return;
        }
        const res = await axios.post(`${API}/deployments/${dep.id}/start`, { confirm_existing_server: isExisting });
        toast.success(res.data.message);
      } else {
        const res = await axios.post(`${API}/deployments/${dep.id}/${action}`);
        toast.success(res.data.message);
      }
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const lastRelease = (dep.releases || [])[dep.releases?.length - 1];

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid={`deployment-card-${dep.slug}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-white font-medium flex items-center gap-2">
              <Rocket className="w-4 h-4 text-orange-400" /> {dep.app_name}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1 truncate">
              <Github className="w-3 h-3 shrink-0" /> {dep.repo_url.replace("https://", "")} · {dep.branch}
            </p>
            <p className="text-xs text-zinc-400 font-mono flex items-center gap-1 mt-0.5">
              <Globe className="w-3 h-3" /> {dep.domain}
              {dep.server_ip && <span className="text-zinc-600">→ {dep.server_ip}</span>}
            </p>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${STATUS_COLORS[dep.status] || STATUS_COLORS.draft}`}
            data-testid={`deployment-status-${dep.slug}`}>
            {dep.status}
          </span>
        </div>

        {dep.integration_key && (
          <div className="flex items-center gap-2 bg-black/40 rounded px-2 py-1">
            <KeyRound className="w-3 h-3 text-zinc-500 shrink-0" />
            <span className="text-[10px] text-zinc-400 font-mono truncate">{dep.integration_key}</span>
            <button onClick={() => { navigator.clipboard.writeText(dep.integration_key); toast.success("Integration key copied"); }}
              data-testid={`copy-key-${dep.slug}`}>
              <Copy className="w-3 h-3 text-zinc-500 hover:text-white" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {dep.status === "draft" && (
            <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-500" disabled={busy}
              onClick={() => act("start")} data-testid={`launch-btn-${dep.slug}`}>
              <Play className="w-3 h-3 mr-1" /> Launch
            </Button>
          )}
          {(dep.status === "failed" || dep.status === "cancelled") && (
            <Button size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-500" disabled={busy}
              onClick={() => act("start", "Re-run the full pipeline from the beginning?")} data-testid={`retry-btn-${dep.slug}`}>
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          )}
          {dep.status === "running" && (
            <Button size="sm" variant="outline" className="h-7 text-xs border-yellow-500/30 text-yellow-400" disabled={busy}
              onClick={() => act("cancel")} data-testid={`cancel-btn-${dep.slug}`}>
              Cancel
            </Button>
          )}
          {dep.status === "completed" && (
            <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-500" disabled={busy || dep.release_running}
              onClick={() => act("release", "Release: git pull → rebuild → restart → health check. Continue?")}
              data-testid={`release-btn-${dep.slug}`}>
              {dep.release_running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UploadCloud className="w-3 h-3 mr-1" />}
              Release Update
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-400"
            onClick={() => setExpanded(!expanded)} data-testid={`toggle-steps-${dep.slug}`}>
            {expanded ? "Hide steps" : "Show steps"} <ChevronRight className={`w-3 h-3 ml-1 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </Button>
          {dep.status !== "running" && (
            <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-600 hover:text-red-400 ml-auto" disabled={busy}
              onClick={() => act("delete", "Delete this deployment record? (The server/app itself is NOT touched)")}
              data-testid={`delete-btn-${dep.slug}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>

        {expanded && <ProgressView dep={dep} />}
        {expanded && lastRelease && (
          <div className="text-[10px] text-zinc-500 border-t border-white/5 pt-2">
            Last release: <span className={lastRelease.status === "success" ? "text-green-400" : lastRelease.status === "failed" ? "text-red-400" : "text-blue-400"}>{lastRelease.status}</span> · {new Date(lastRelease.at).toLocaleString()}
            {lastRelease.log && <pre className="mt-1 bg-black/50 rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap">{lastRelease.log}</pre>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DnsProvidersCard({ doConfigured }) {
  const [cf, setCf] = useState({ configured: false, token_preview: null });
  const [cfToken, setCfToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadCf = useCallback(() => {
    axios.get(`${API}/dns/cloudflare/config`).then((r) => setCf(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadCf(); }, [loadCf]);

  const saveCf = async () => {
    if (!cfToken.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/dns/cloudflare/config`, { token: cfToken.trim() });
      toast.success("Cloudflare token saved & validated");
      setCfToken("");
      loadCf();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save token");
    } finally {
      setBusy(false);
    }
  };

  const removeCf = async () => {
    if (!window.confirm("Remove the Cloudflare API token?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/dns/cloudflare/config`);
      toast.success("Cloudflare token removed");
      loadCf();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove token");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-[#0c0c0e] border-white/5" data-testid="dns-providers-card">
      <CardContent className="p-4">
        <button className="w-full flex items-center justify-between" onClick={() => setExpanded(!expanded)}
          data-testid="dns-providers-toggle">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-white font-medium">DNS Auto-Setup</span>
            <span className="text-[10px] text-zinc-500">A records are created automatically during deploys</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${doConfigured ? "text-green-400 bg-green-500/10 border-green-500/20" : "text-zinc-500 bg-zinc-500/10 border-zinc-500/20"}`}
              data-testid="dns-do-status">
              DO DNS {doConfigured ? "ready" : "no token"}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cf.configured ? "text-green-400 bg-green-500/10 border-green-500/20" : "text-zinc-500 bg-zinc-500/10 border-zinc-500/20"}`}
              data-testid="dns-cf-status">
              Cloudflare {cf.configured ? "ready" : "no token"}
            </span>
            <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </div>
        </button>
        {expanded && (
          <div className="mt-3 pt-3 border-t border-white/5 grid md:grid-cols-2 gap-4">
            <div className="text-xs text-zinc-400 space-y-1">
              <p className="text-zinc-300 font-medium">DigitalOcean DNS</p>
              <p>Uses the same DO API token. {doConfigured ? "Configured — zones hosted at DO are handled automatically." : <>Not configured yet — add it on the <a href="/cloud" className="text-blue-400 underline">Cloud page</a>.</>}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-zinc-300 font-medium">Cloudflare (for stancor.in &amp; friends)</p>
              {cf.configured ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-400 font-mono bg-black/40 rounded px-2 py-1">{cf.token_preview}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-zinc-500 hover:text-red-400" disabled={busy}
                    onClick={removeCf} data-testid="cf-token-remove-btn">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input className="bg-black/40 border-white/10 text-white text-xs h-8" type="password"
                    placeholder="Cloudflare API token (Zone:DNS:Edit)" value={cfToken}
                    onChange={(e) => setCfToken(e.target.value)} data-testid="cf-token-input" />
                  <Button size="sm" className="h-8 text-xs bg-cyan-600 hover:bg-cyan-500" disabled={busy || !cfToken.trim()}
                    onClick={saveCf} data-testid="cf-token-save-btn">
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-zinc-600">Create at dash.cloudflare.com → My Profile → API Tokens → "Edit zone DNS" template. Records are created DNS-only (grey cloud) so SSL issuance works.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const WIZARD_STEPS = ["Source", "Target", "Domain & Env", "Review"];

function Wizard({ open, onClose, options, onCreated }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    app_name: "", repo_url: "", branch: "main", repo_token: "",
    target_mode: "new_droplet", server_id: "", region: "blr1", size: "s-2vcpu-4gb",
    domain: "", ssl_enabled: true, ssl_email: "admin@stancor.in", backend_port: 8001,
    env_backend: "", env_frontend: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.domain && !form.env_frontend) {
      set("env_frontend", `REACT_APP_BACKEND_URL=https://${form.domain}`);
    }
  }, [step]); // eslint-disable-line

  const canNext = () => {
    if (step === 0) return form.app_name.trim() && form.repo_url.trim().startsWith("http");
    if (step === 1) return form.target_mode === "new_droplet" || form.server_id;
    if (step === 2) return /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.domain.trim());
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/deployments`, form);
      toast.success(`Deployment '${form.app_name}' created — hit Launch when ready`);
      onCreated(res.data);
      onClose();
      setStep(0);
      setForm({ app_name: "", repo_url: "", branch: "main", repo_token: "", target_mode: "new_droplet", server_id: "", region: "blr1", size: "s-2vcpu-4gb", domain: "", ssl_enabled: true, ssl_email: "admin@stancor.in", backend_port: 8001, env_backend: "", env_frontend: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create deployment");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-black/40 border-white/10 text-white text-sm";
  const label = (t) => <p className="text-xs text-zinc-400 mb-1">{t}</p>;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-lg" data-testid="deploy-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Rocket className="w-4 h-4 text-orange-400" /> Deploy New App
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1 mb-2">
          {WIZARD_STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-1">
              <div className={`h-1 flex-1 rounded ${i <= step ? "bg-orange-500" : "bg-white/10"}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 -mt-2">Step {step + 1} of 4 — {WIZARD_STEPS[step]}</p>

        {step === 0 && (
          <div className="space-y-3">
            <div>{label("App name")}
              <Input className={inputCls} placeholder="Expense Tracker" value={form.app_name}
                onChange={(e) => set("app_name", e.target.value)} data-testid="wizard-app-name" /></div>
            <div>{label("GitHub repo URL (from Emergent 'Save to GitHub')")}
              <Input className={inputCls} placeholder="https://github.com/user/repo" value={form.repo_url}
                onChange={(e) => set("repo_url", e.target.value)} data-testid="wizard-repo-url" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>{label("Branch")}
                <Input className={inputCls} value={form.branch} onChange={(e) => set("branch", e.target.value)} data-testid="wizard-branch" /></div>
              <div>{label("Access token (private repos)")}
                <Input className={inputCls} type="password" placeholder="ghp_... (optional)" value={form.repo_token}
                  onChange={(e) => set("repo_token", e.target.value)} data-testid="wizard-repo-token" /></div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => set("target_mode", "new_droplet")}
                className={`rounded-lg border p-3 text-left ${form.target_mode === "new_droplet" ? "border-orange-500/60 bg-orange-500/5" : "border-white/10 hover:border-white/20"}`}
                data-testid="wizard-mode-new-droplet">
                <p className="text-sm text-white flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New droplet</p>
                <p className="text-[10px] text-zinc-500 mt-1">Provision a fresh DO server{!options.do_configured && " (needs DO token)"}</p>
              </button>
              <button onClick={() => set("target_mode", "existing_server")}
                className={`rounded-lg border p-3 text-left ${form.target_mode === "existing_server" ? "border-orange-500/60 bg-orange-500/5" : "border-white/10 hover:border-white/20"}`}
                data-testid="wizard-mode-existing-server">
                <p className="text-sm text-white flex items-center gap-1.5"><Server className="w-3.5 h-3.5" /> Existing server</p>
                <p className="text-[10px] text-zinc-500 mt-1">Deploy alongside apps on a connected server</p>
              </button>
            </div>
            {form.target_mode === "new_droplet" ? (
              <>
                {!options.do_configured && (
                  <div className="flex items-start gap-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>No DigitalOcean token yet. You can still save this deployment — the Launch button activates the moment the token is added on the Cloud page.</span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>{label("Region")}
                    <Select value={form.region} onValueChange={(v) => set("region", v)}>
                      <SelectTrigger className={inputCls} data-testid="wizard-region"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-white/10 text-white">
                        {options.regions.map((r) => <SelectItem key={r.slug} value={r.slug}>{r.name} ({r.slug})</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div>{label("Size")}
                    <Select value={form.size} onValueChange={(v) => set("size", v)}>
                      <SelectTrigger className={inputCls} data-testid="wizard-size"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#18181b] border-white/10 text-white">
                        {options.sizes.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.label || s.slug}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                </div>
              </>
            ) : (
              <div>{label("Target server")}
                <Select value={form.server_id} onValueChange={(v) => set("server_id", v)}>
                  <SelectTrigger className={inputCls} data-testid="wizard-server"><SelectValue placeholder="Choose a connected server" /></SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10 text-white">
                    {options.servers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.ip_address})</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-amber-400/80 mt-1">Tip: pick a unique backend port on shared servers (next step keeps 8001 by default).</p>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>{label("Domain")}
                <Input className={inputCls} placeholder="app.stancor.in" value={form.domain}
                  onChange={(e) => { set("domain", e.target.value); set("env_frontend", `REACT_APP_BACKEND_URL=https://${e.target.value}`); }}
                  data-testid="wizard-domain" /></div>
              <div>{label("Backend port")}
                <Input className={inputCls} type="number" value={form.backend_port}
                  onChange={(e) => set("backend_port", parseInt(e.target.value) || 8001)} data-testid="wizard-port" /></div>
            </div>
            <div className="flex items-center justify-between bg-black/40 rounded p-2">
              <div>
                <p className="text-xs text-white">Free SSL (Let's Encrypt)</p>
                <p className="text-[10px] text-zinc-500">A record is auto-created via DO DNS or Cloudflare (if configured), then SSL is issued. Skipped safely otherwise.</p>
              </div>
              <Switch checked={form.ssl_enabled} onCheckedChange={(v) => set("ssl_enabled", v)} data-testid="wizard-ssl-toggle" />
            </div>
            <div>{label("Backend .env (MONGO_URL & DB_NAME auto-added if missing)")}
              <Textarea className={`${inputCls} font-mono text-xs h-20`} placeholder={"OPENAI_API_KEY=sk-...\nJWT_SECRET=..."}
                value={form.env_backend} onChange={(e) => set("env_backend", e.target.value)} data-testid="wizard-env-backend" /></div>
            <div>{label("Frontend .env")}
              <Textarea className={`${inputCls} font-mono text-xs h-14`} value={form.env_frontend}
                onChange={(e) => set("env_frontend", e.target.value)} data-testid="wizard-env-frontend" /></div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 text-xs" data-testid="wizard-review">
            {[
              ["App", form.app_name], ["Repo", `${form.repo_url} (${form.branch})`],
              ["Target", form.target_mode === "new_droplet" ? `New droplet — ${form.region} / ${form.size}` : `Existing server — ${options.servers.find((s) => s.id === form.server_id)?.name || ""}`],
              ["Domain", `https://${form.domain} (SSL ${form.ssl_enabled ? "on" : "off"})`],
              ["Backend port", form.backend_port],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2 border-b border-white/5 pb-1.5">
                <span className="text-zinc-500 w-24 shrink-0">{k}</span>
                <span className="text-zinc-200 break-all">{v}</span>
              </div>
            ))}
            <p className="text-[10px] text-zinc-500 pt-1">
              Pipeline: provision → stack install (Nginx, MongoDB, Node, Python, ufw, fail2ban) → clone → .env → build → supervisor+Nginx → SSL → auto-register in Servers & Connected Systems → health check.
            </p>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" className="text-zinc-400" disabled={step === 0}
            onClick={() => setStep(step - 1)} data-testid="wizard-back-btn">
            <ChevronLeft className="w-3 h-3 mr-1" /> Back
          </Button>
          {step < 3 ? (
            <Button size="sm" className="bg-orange-600 hover:bg-orange-500" disabled={!canNext()}
              onClick={() => setStep(step + 1)} data-testid="wizard-next-btn">
              Next <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          ) : (
            <Button size="sm" className="bg-orange-600 hover:bg-orange-500" disabled={saving}
              onClick={submit} data-testid="wizard-create-btn">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Rocket className="w-3 h-3 mr-1" />}
              Create Deployment
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DeployApps() {
  const [deployments, setDeployments] = useState([]);
  const [options, setOptions] = useState({ do_configured: false, servers: [], regions: [], sizes: [] });
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, o] = await Promise.all([
        axios.get(`${API}/deployments`),
        axios.get(`${API}/deploy/options`),
      ]);
      setDeployments(d.data);
      setOptions(o.data);
    } catch {
      toast.error("Failed to load deployments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const anyRunning = deployments.some((d) => d.status === "running" || d.release_running);
    if (!anyRunning) return;
    const t = setInterval(() => {
      axios.get(`${API}/deployments`).then((r) => setDeployments(r.data)).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [deployments]);

  return (
    <div className="p-6 space-y-5 max-w-5xl" data-testid="deploy-apps-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Rocket className="w-6 h-6 text-orange-400" /> Deploy Apps
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            One-click GitHub → self-hosted deployment: droplet, stack, build, Nginx, SSL and helpdesk registration.
          </p>
        </div>
        <Button className="bg-orange-600 hover:bg-orange-500" onClick={() => setWizardOpen(true)} data-testid="new-deployment-btn">
          <Plus className="w-4 h-4 mr-1" /> New Deployment
        </Button>
      </div>

      {!options.do_configured && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="do-token-banner">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200/90">
            <p className="font-medium text-amber-300">Wizard is ready — waiting for your DigitalOcean token</p>
            <p className="mt-0.5">Add the token on the <a href="/cloud" className="underline">Cloud page</a> to activate new-droplet provisioning. Deploying to an <span className="font-medium">existing connected server</span> works right now without it.</p>
          </div>
        </div>
      )}

      <DnsProvidersCard doConfigured={options.do_configured} />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-zinc-600 animate-spin" /></div>
      ) : deployments.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5 border-dashed">
          <CardContent className="py-14 text-center space-y-2">
            <Rocket className="w-8 h-8 text-zinc-700 mx-auto" />
            <p className="text-sm text-zinc-400">No deployments yet</p>
            <p className="text-xs text-zinc-600">Build an app in Emergent → Save to GitHub → deploy it here in one click.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {deployments.map((d) => <DeploymentCard key={d.id} dep={d} onRefresh={load} />)}
        </div>
      )}

      <Wizard open={wizardOpen} onClose={() => setWizardOpen(false)} options={options} onCreated={load} />
    </div>
  );
}
